import { type FunctionComponent, type HTMLAttributes, type SyntheticEvent, type ReactElement, useState } from 'react';
import { type ImageFieldsFragment } from '../../../__generated__/graphql-client-types';
import { defaultCloudinaryOptions } from '../../../constants/images';
import { type CloudinaryOptions, generateCloudinaryImageProps } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import { formatPrice } from '../../../helpers/general-helper/general-helper';
import { Link } from '../../common-components/link/link.component';
import { Popover } from '../../common-components/popover/popover.component';
import { CloudinaryImage, type CloudinaryImageProps } from '../../images/cloudinary-image/cloudinary-image.component';
import { Rating, type RatingProps } from '../rating/rating.component';
import { finishesPopOver } from './product-information.css';

export type ProductTitleProps = {
	title: string;
	brandName?: string | null;
	contentClass?: string;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductTitle: FunctionComponent<ProductTitleProps> = ({
	className = 'dib f5 mt2',
	contentClass = 'tc2-title tl',
	brandName,
	title
}) => (
	<div className={`fw2 theme-black ${className}`}>
		<span className={contentClass}>
			{brandName && <strong className="pr1 f-normal">{brandName}</strong>}
			{title}
		</span>
	</div>
);

export type ProductModelProps = {
	modelNumber?: string | null;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductModel: FunctionComponent<ProductModelProps> = ({ className = 'tc1-title f6 mt1', modelNumber }) =>
	modelNumber ? (
		<span className={`theme-grey ${className}`}>
			Model:<strong className="ml1 f-normal">{modelNumber}</strong>
		</span>
	) : null;

export type ProductImageProps = {
	image?: ImageFieldsFragment | null;
	options?: CloudinaryOptions;
	cloudinaryImageProps?: Partial<CloudinaryImageProps>;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductImage: FunctionComponent<ProductImageProps> = ({
	className = '',
	image,
	options = defaultCloudinaryOptions,
	cloudinaryImageProps = {}
}) => (image ? <CloudinaryImage className={className} {...generateCloudinaryImageProps(image, options, cloudinaryImageProps)} /> : null);

export type ProductStartingAtPriceProps = {
	startingAtPrice?: number;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductStartingAtPrice: FunctionComponent<ProductStartingAtPriceProps> = ({ className = 'f5 mt2', startingAtPrice }) =>
	startingAtPrice ? <span className={`b ${className}`}>Starting at {formatPrice(startingAtPrice)}</span> : null;

export type ProductPriceProps = {
	price?: { current: number };
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductPrice: FunctionComponent<ProductPriceProps> = ({ className = 'theme-grey f6', price }) =>
	price?.current ? (
		<div className={className} data-testid="product-option-price">
			{formatPrice(price.current)}
		</div>
	) : null;

export type ProductTotalProps = {
	value: number;
	label?: string;
	totalItems?: number;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductTotal: FunctionComponent<ProductTotalProps> = ({
	className = 'justify-start mt3',
	value,
	label = 'Subtotal',
	totalItems = 0
}) => {
	const itemCountText = totalItems > 0 ? ` (${totalItems} item${totalItems > 1 ? 's' : ''}):` : ':';
	const formatLabel = `${label}${itemCountText}`;
	return (
		<section className={`flex flex-row items-center w-100 f4 ${className}`} data-testid="product-total">
			<strong>{formatLabel}</strong>
			<span className="b ml2">{formatPrice(value)}</span>
		</section>
	);
};

export type ProductFinishProps = {
	id: number;
	title: string;
	url: string;
};

export type ProductFinishListProps = {
	finishes: ProductFinishProps[];
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductFinishList: FunctionComponent<ProductFinishListProps> = ({ className = 'ma0 f6 pl4', finishes }) => (
	<ul className={`flex flex-column w-100 ${finishesPopOver} ${className}`}>
		{finishes.map((finish) => (
			<li key={finish.id}>
				<Link url={finish.url} underlineHover={true} className="tl tc1-title" color="grey-darker">
					{finish.title}
				</Link>
			</li>
		))}
	</ul>
);

export type ProductFinishesPopoverProps = {
	finishes: ProductFinishProps[];
	direction?: 'top' | 'bottom';
	triggerElementClassName?: string;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductFinishesPopover: FunctionComponent<ProductFinishesPopoverProps> = ({
	className = 'pointer mt3',
	triggerElementClassName = 'theme-grey f6 underline',
	direction = 'top',
	finishes
}) => {
	const [showFinishes, setShowFinishes] = useState(false);
	const onToggle = (ev?: SyntheticEvent) => ev && ev.stopPropagation();
	const triggerElement: ReactElement = <span className={triggerElementClassName}>{finishes.length} Finishes</span>;

	return finishes.length > 1 ? (
		<div className={className}>
			<Popover
				toggleElement={triggerElement}
				onToggle={onToggle}
				direction={direction}
				isVisible={showFinishes}
				setIsVisible={setShowFinishes}>
				<ProductFinishList finishes={finishes} />
			</Popover>
		</div>
	) : null;
};

export type ProductRatingProps = {
	count?: number;
	rating?: number;
} & Partial<Pick<RatingProps, 'readOnly' | 'url' | 'abbreviatedReviewCount'>> &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductRating: FunctionComponent<ProductRatingProps> = ({
	className = '',
	count = 0,
	rating = 0,
	readOnly = true,
	url,
	abbreviatedReviewCount = true
}) =>
	rating ? (
		<Rating
			className={className}
			ratingValue={rating}
			reviewCount={count}
			readOnly={readOnly}
			url={url}
			abbreviatedReviewCount={abbreviatedReviewCount}
		/>
	) : null;
